// components/DepartmentManagement.js
import Icon from 'components/appIcon';
import Button from 'components/ui/button';
import Modal from 'components/ui/modal';
import { useAuth } from 'components/ui/navigationGuard';
import Notification from 'components/ui/notification';
import Table from 'components/ui/table';
import useDepartmentManagementLogic from 'hooks/useDepartment';
import AdminNavigation from 'layouts/adminNavigation';
import { useNavigate } from 'react-router-dom';
import DepartmentModel from './components/departmentModel';

const DepartmentManagement = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Use the custom hook to get all the state and functions
  const {
    isNavCollapsed,
    departments,
    loading,
    modalState,
    notification,
    formData,
    errors,
    columns,
    setIsNavCollapsed,
    setModalState,
    closeNotification,
    showNotification,
    loadDepartments,
    handleInputChange,
    validateForm,
    handleSave,
    handleAddDepartment,
    handleEditDepartment,
    handleDeleteDepartment
  } = useDepartmentManagementLogic();

  if (loading) {
    return (
      <>
        <AdminNavigation
          user={user}
          isCollapsed={isNavCollapsed}
          onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
        />
        <div className={`min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 transition-all duration-200 ${isNavCollapsed ? 'lg:ml-16' : 'lg:ml-64'} pt-16`}>
          <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-muted-foreground">Loading departments...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavigation
        user={user}
        isCollapsed={isNavCollapsed}
        onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
      />
      <div className={`min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 transition-all duration-200 ${isNavCollapsed ? 'lg:ml-16' : 'lg:ml-64'} pt-16`}>
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Department Management</h1>
              <p className="text-slate-500 mt-1">Manage company departments</p>
            </div>
            <div className="mt-4 sm:mt-0">
              <Button
                variant="default"
                onClick={handleAddDepartment}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Icon name="Plus" size={16} className="mr-2" />
                Add Department
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <Table
              columns={columns}
              data={departments}
              emptyMessage="No departments found"
            />
          </div>
        </div>
      </div>

      {/* Department Modal */}
      <Modal isOpen={modalState.isOpen} onClose={() => setModalState({ isOpen: false, department: null, mode: "add" })}>
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">
            {modalState.mode === "add" ? "Add New Department" : "Edit Department"}
          </h2>
          
          <DepartmentModel formData={formData} errors={errors} handleInputChange={handleInputChange} />
          
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setModalState({ isOpen: false, department: null, mode: "add" })}
            >
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {modalState.mode === "add" ? "Add Department" : "Save Changes"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Notification Component */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}
    </>
  );
};

export default DepartmentManagement;